import React from "react";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection, useLiveQuery } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";

import { settingsApi } from "../api";

export type TimeZone = {
	id: string;
	hasIanaId: boolean;
	displayName: string;
	standardName: string;
	daylightName: string;
	baseUtcOffset: string;
	supportsDaylightSavingTime: boolean;
};

const utcOffsetRegex = /^\(UTC[+-]\d{2}:\d{2}\)\s*/;

const queryClient = new QueryClient();

const timeZoneCollection = createCollection(
	queryCollectionOptions({
		queryKey: ["timezones"],
		queryFn: async () => {
			const response = await settingsApi.get<TimeZone[]>("common", "timezones");
			return response;
		},
		queryClient,
		getKey: (item) => item.id,
		staleTime: 1000 * 60 * 120,
		syncMode: "on-demand",
	}),
);

export function useTimeZones() {
	const timeZones = useLiveQuery((q) =>
		q
			.from({
				timeZone: timeZoneCollection,
			})
			.select(({ timeZone }) => ({ ...timeZone })),
	);

	const value = React.useMemo(() => {
		return {
			data: timeZones.data,
			lookup: timeZones.data.reduce<Record<string, string>>((acc, tz) => {
				acc[tz.id] = tz.displayName.replace(utcOffsetRegex, "");
				return acc;
			}, {}),
		};
	}, [timeZones.data]);

	return value;
}
